import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { recordReportVersion } from "@/lib/reportVersion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";
import { deriveSuccessCopy } from "@/lib/paymentSuccessCopy";
import { useT, tr, getLocale } from "@/lib/i18n";

export const PaymentSuccess = () => {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [tries, setTries] = useState(0);
  const recordedRef = useRef(false);
  useT();

  useEffect(() => {
    if (!sessionId) { navigate("/"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setStatus(r.data);
        if (r.data.payment_status === "paid") {
          if (r.data.project_paid === true && r.data.project_id && !recordedRef.current) {
            recordedRef.current = true;
            recordReportVersion(r.data.project_id, "single");
          }
          track("report_purchase", { package_id: r.data.package_id, session_id: sessionId });
          return;
        }
      } catch (err) { /* keep polling — network/webhook may be delayed */
        console.debug("[payments] status poll retry", err?.message);
      }
      setTries((t) => t + 1);
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const paid = status?.payment_status === "paid";
  const copy = deriveSuccessCopy(status || {}, getLocale());

  return (
    <div className="App">
      <Header />
      <div className="max-w-[720px] mx-auto px-6 py-20 text-center" data-testid="payment-success">
        {paid ? (
          <>
            <CheckCircle2 size={48} className="mx-auto" style={{ color: "var(--brand-green)" }} />
            <h1 className="font-display text-3xl mt-4" data-testid="payment-success-headline">{copy.headline}</h1>
            <p className="mt-2" style={{ color: "var(--muted)" }} data-testid="payment-success-subhead">
              {copy.subhead}
            </p>
            <div className="mt-8 flex gap-3 justify-center flex-wrap">
              {copy.ctaLabel && copy.ctaTarget && (
                <button
                  onClick={() => navigate(copy.ctaTarget)}
                  className="btn-primary"
                  data-testid="payment-success-cta"
                >
                  {copy.ctaLabel}
                </button>
              )}
              {/* Always offer an escape hatch to /projects for the buyer. */}
              {copy.ctaTarget !== "/projects" && (
                <button onClick={() => navigate("/projects")} className="btn-secondary" data-testid="payment-success-projects">
                  {tr("Go to my projects", "Aller à mes projets")}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <Loader2 size={40} className="mx-auto animate-spin" style={{ color: "var(--brand-green)" }} />
            <h1 className="font-display text-2xl mt-4">{copy.headline}</h1>
            <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>
              {tries > 8 ? tr("Taking longer than usual — the webhook might be delayed. Refresh in a moment.", "Cela prend plus de temps que d'habitude — la confirmation du paiement est peut-être retardée. Actualisez dans un instant.") : tr("Please wait — this usually takes a few seconds.", "Veuillez patienter — cela prend généralement quelques secondes.")}
            </p>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};
